import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { getCandidateDisplayName } from "../../helpers/RequirementEligibilityHelper";
import { groupStatusesForMenu } from "../../helpers/RequirementKanbanHelper";

export default function SubmitCandidateToRequirementModal({
  show,
  onHide,
  candidate,
  requirement,
  statuses = [],
  saving = false,
  onConfirm,
}) {
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [remark, setRemark] = useState("");

  useEffect(() => {
    if (show) {
      setSelectedStatusId("");
      setRemark("");
    }
  }, [show, candidate?._candidate_id]);

  const menuGroups = useMemo(() => groupStatusesForMenu(statuses), [statuses]);

  const selectedStatus = useMemo(
    () => statuses.find((row) => String(row._status_id) === String(selectedStatusId)),
    [statuses, selectedStatusId]
  );

  const schedulesInterview = Number(selectedStatus?.status_schedule_interview) === 1;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedStatus) {
      return;
    }

    onConfirm?.({
      status: selectedStatus,
      remark: remark.trim(),
      schedulesInterview,
    });
  };

  if (!candidate || !requirement) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" className="cms-modal-form">
      <Modal.Header closeButton>
        <Modal.Title>Submit to job</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-3">
            Submit <strong>{getCandidateDisplayName(candidate)}</strong> to{" "}
            <strong>{requirement.requirement_name}</strong>
            {requirement.client_name ? (
              <>
                {" "}
                (<span className="text-muted">{requirement.client_name}</span>)
              </>
            ) : null}
            ?
          </p>

          <div className="alert alert-light border small mb-3">
            Candidates already submitted to this requirement cannot be submitted again. This will
            link them to this client and requirement with the status you choose.
          </div>

          <div className="mb-3">
            <label htmlFor="submit-req-status" className="form-label">
              Status <span className="text-danger">*</span>
            </label>
            <select
              id="submit-req-status"
              className="form-select"
              value={selectedStatusId}
              onChange={(event) => setSelectedStatusId(event.target.value)}
              required
            >
              <option value="">Select status…</option>
              {Object.values(menuGroups).map((group) =>
                group.items.length > 0 ? (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map((status) => (
                      <option key={status._status_id} value={status._status_id}>
                        {status.status_name}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              )}
            </select>
          </div>

          <div className="mb-3">
            <label htmlFor="submit-req-remark" className="form-label">
              Remark <span className="text-muted fw-normal">(optional)</span>
            </label>
            <textarea
              id="submit-req-remark"
              className="form-control"
              rows={3}
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              placeholder="Note for this submission"
            />
          </div>

          {schedulesInterview ? (
            <div className="small text-warning">
              <i className="ri-calendar-event-line me-1" />
              After submit, you will be asked to schedule the interview for this status.
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <button type="button" className="btn btn-link btn-sm" onClick={onHide} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || !selectedStatus}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Submitting…
              </>
            ) : schedulesInterview ? (
              "Submit & schedule interview"
            ) : (
              "Confirm submit"
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
